import { authentication, HostClientType } from '@microsoft/teams-js';
import { getMSTeamsUserContext, isMSTeams } from 'utils/MSTeams';
import { getUserLocale } from 'i18n/getUserLocale';
import getSubdomainFromUrl from 'utils/getSubdomainFromUrl';
import { useSubdomainInfo } from 'LoggedOut/SignUp/useSubdomainInfo';
import { tracking } from 'App/Tracking';
import { logException } from 'App/logging';
import RoutePath from 'App/RoutePath';
import {
  authenticationErrorStorageCode,
  AuthErrorCode,
  MSTeamsAuthPopupErrorMessage,
} from 'LoggedOut/Login/AuthError';
import useUniversalLogin from './useUniversalLogin';

/**
 * Which journey the user was in when they started SSO. Sign up and login share
 * `sso-continue-sso-clicked`, so without this the funnels can't be told apart.
 *
 * There is deliberately no `Login`: nothing can currently prove login intent.
 * `/sso` is the target of both the login button and the sign-up redirects for
 * SSO-only orgs, so it reports `Unknown` rather than claiming a login it can't
 * verify. Add `Login` when a call site can honestly assert it.
 */
export enum SSOOrigin {
  SignUp = 'sign-up',
  Unknown = 'unknown',
}

export const useSSO = () => {
  const subdomain = getSubdomainFromUrl();
  const { authProviderOrganisationId, ssoProviderName, loading } =
    useSubdomainInfo({ subdomain });
  const { loginWithUniversalLogin, loginWithPopupWindow } = useUniversalLogin();

  const returnTo =
    new URLSearchParams(location.search).get('redirect') || undefined;

  // extracted verbatim from LoginViaSSO.tsx
  const startSSOInMSTeams = async (): Promise<void> => {
    try {
      // Compute locale here so we capture the MS Teams SDK context
      // (the auth window opened below cannot read it).
      const unmindLocale = getUserLocale();
      const authenticateUrl = `${window.location.origin}${
        RoutePath.MSTeamsSSO
      }?unmind_locale=${encodeURIComponent(unmindLocale)}`;
      const authenticationResult = await authentication.authenticate({
        isExternal: false,
        url: authenticateUrl,
      });

      if (authenticationResult === 'success') {
        window.location.assign(RoutePath.SSOLoginHome);
      }
    } catch (e) {
      const authenticationError = localStorage.getItem(
        authenticationErrorStorageCode,
      );

      localStorage.removeItem(authenticationErrorStorageCode);

      if (authenticationError) {
        const { errorDescription, authErrorParam } =
          JSON.parse(authenticationError);

        if (errorDescription === 'EMAIL_VERIFICATION_REQUIRED') {
          window.location.assign(
            RoutePath.VerifyAccountOwnershipEmailConfirmation,
          );
        } else {
          window.location.assign(`${RoutePath.LoginWithSSO}${authErrorParam}`);
        }

        return;
      }

      if ((e as Error).message === MSTeamsAuthPopupErrorMessage) {
        window.location.assign(
          `${RoutePath.LoginWithSSO}?error_code=${AuthErrorCode.AUTH_CANCELLED_BY_USER}`,
        );

        return;
      }
    }
  };

  /**
   * `origin` is required rather than defaulted so that call sites can't pass
   * this straight to `onClick` and hand a MouseEvent in as the origin.
   *
   * Failures are logged here rather than at the call sites so that every caller
   * gets Sentry reporting without having to remember it. Callers that can show
   * the user a message pass `onError`.
   *
   * The `await`s below are load-bearing: `return somePromise` inside a `try`
   * hands the promise to the caller without awaiting it locally, so rejections
   * would skip this `catch` and become unhandled again.
   */
  const startSSO = async (
    origin: SSOOrigin,
    { onError }: { onError?(error: unknown): void } = {},
  ) => {
    tracking.track('sso-continue-sso-clicked', { subdomain, origin });

    try {
      if (!isMSTeams()) {
        return await loginWithUniversalLogin({
          authProviderOrganisationId,
          returnTo,
        });
      }

      const context = getMSTeamsUserContext();
      const isWeb = context?.app.host.clientType === HostClientType.web;

      // Due to a bug on web we've had to reintroduce the auth0 popup flow for SSO
      // details here: https://unmind.atlassian.net/browse/ENG-12806
      if (isWeb) {
        return await loginWithPopupWindow({ authProviderOrganisationId });
      }

      return await startSSOInMSTeams();
    } catch (error) {
      logException(error);
      onError?.(error);

      return undefined;
    }
  };

  return { startSSO, ssoProviderName, loading };
};

export default useSSO;
